import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnInit,
  Optional,
  output,
  Self,
  signal,
  TemplateRef,
  untracked,
  viewChildren,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  ValidatorFn,
} from '@angular/forms';
import { HxGridTabNavigation } from '../../a11y';
import { ControlChange } from '../../base/control-change-event';
import { HxBaseControl } from '../../base/hx-base-control';
import { DateAdapter } from '../date-picker/adapters/abstract-date-adapter';
import { HxDatePicker, DateRange, DateSelection } from '../date-picker/date-picker';
import { HX_DATE_ADAPTER } from '../date-picker/date-picker-tokens';
import { HxCheckbox } from '../checkbox/checkbox';
import { HxIcon } from '../icon/icon';
import { HxInput } from '../input/input';
import { HxRadio } from '../radio/radio';
import { HxRadioGroup } from '../radio/radio-group';
import { HxRating } from '../rating/rating';
import { HxDropdownWithLabelComponent } from '../select/dropdown-with-label/dropdown-with-label.component';
import { isOtherOption } from '../select/shared/option.model';
import { ValidationMode } from '../table-grid';
import { HxTableCellDirective } from '../table/directives/table-cell';
import { HxTableHeaderDirective } from '../table/directives/table-header';
import { HxTableRowDirective } from '../table/directives/table-row';
import { HxHeaderCell } from '../table/header-cell';
import { HxTableComponent } from '../table/table';
import { RuleEvaluatorService } from './rule-evaluator.service';
import type {
  CellControlProps,
  CellData,
  CellDataOrValue,
  CellMode,
  CellSettings,
  CellValidationError,
  CellValue,
  ColumnInputType,
  ConditionalTableGridResponse,
  DateCalculationConfig,
  EnhancedColumnDefinition,
  EnhancedRowData,
  DateCalculationUnit,
  InputOption,
} from './types';

/**
 * HxConditionalTableGrid
 *
 * A configuration-driven table grid supporting heterogeneous column input types (checkbox, radio, dropdown, text,
 * rating) with per-row rule-based conditional disabling.
 *
 * @extends HxBaseControl
 * @selector hx-conditional-table-grid
 */
@Component({
  selector: 'hx-conditional-table-grid',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HxTableComponent,
    HxTableCellDirective,
    HxTableHeaderDirective,
    HxTableRowDirective,
    HxHeaderCell,
    HxCheckbox,
    HxInput,
    HxRadio,
    HxRadioGroup,
    HxDropdownWithLabelComponent,
    HxRating,
    HxDatePicker,
    HxIcon,
    HxGridTabNavigation,
  ],
  templateUrl: './conditional-table-grid.html',
  styleUrls: ['./conditional-table-grid.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class HxConditionalTableGrid extends HxBaseControl implements OnInit, Validator {
  private readonly ruleEvaluator = inject(RuleEvaluatorService);
  private readonly dateAdapter = inject<DateAdapter<Date>>(HX_DATE_ADAPTER);
  private readonly _elementRef = inject(ElementRef);

  // --- Signal Inputs ---
  readonly tableId = input<string>('');
  readonly columns = input<EnhancedColumnDefinition[]>([]);
  readonly rows = input<EnhancedRowData[]>([]);
  readonly questionLabel = input<string>('Question');
  readonly showSerialNumber = input<boolean>(true);
  readonly showActions = input<boolean>(false);
  readonly initialResponses = input<Record<string, Record<string, CellDataOrValue>>>({});
  readonly readonlyMode = input<boolean>(false);
  readonly selectAll = input<boolean>(true);
  readonly selectAllColumnIds = input<string[]>([]);
  readonly required = input<boolean>(false);

  /**
   * Unique FHIR linkId for this control instance. Used to key the "Configure Columns" visibility preference in
   * localStorage so each dropped table control retains its own settings.
   */
  readonly linkId = input<string | undefined>(undefined);

  /**
   * Whether to show the "Configure Columns" trigger/popover, letting users hide/show columns. Backward-compatible: when
   * this property is not provided (older saved templates), defaults to true.
   *
   * @default true
   */
  readonly showConfigureColumns = input<boolean>(true);

  /**
   * Whether column widths resized by the user via drag should be saved (alongside hidden columns) and restored on
   * reopen. Backward-compatible: when this property is not provided (older saved templates), defaults to true.
   *
   * @default true
   */
  readonly saveColumnWidths = input<boolean>(true);

  /**
   * Designer-configurable default for whether the header row starts pinned while the body scrolls. End users can still
   * toggle this via the "Configure Columns" menu; their choice is persisted and takes precedence over this default.
   *
   * @default false
   */
  readonly freezeHeader = input<boolean>(false);

  /** Cap applied to the scrollable table body while the header is frozen (passed through to `hx-table`). */
  readonly freezeHeaderMaxHeight = input<string>('min(60vh, 36rem)');

  /** Validation mode: 'none', 'all-rows', 'at-least-one-yes', or 'custom' */
  readonly requiredMode = input<ValidationMode>('all-rows');

  /** Custom validation message to display when validation fails */
  readonly validationMessage = input<string | undefined>(undefined);

  /** Target value for 'at-least-one-yes' validation (defaults to auto-detect from first column) */
  readonly validationTargetValue = input<string | undefined>(undefined);

  /** Force show validation errors even if not touched */
  readonly forceShowErrors = input<boolean>(false);

  /** Per-cell control properties from nested FHIR items. Keyed by rowLinkId → colId → CellControlProps. */
  readonly cellControls = input<Record<string, Record<string, CellControlProps>>>({});

  /** Optional runtime template for rendering additional content beneath a cell control. */
  readonly cellSubcontrolTemplate = input<TemplateRef<unknown> | null>(null);

  /** Cell-specific configurations including mode. Keyed by rowLinkId → colId → CellSettings. */
  readonly cellSettings = input<Record<string, Record<string, CellSettings>>>({});

  // --- Signal Outputs ---
  readonly responseChange = output<ConditionalTableGridResponse>();
  readonly cellStateChange = output<Record<string, Record<string, boolean>>>();

  // --- Internal State ---

  protected readonly dropdowns = viewChildren(HxDropdownWithLabelComponent);

  /**
   * Computed signal that tracks dropdown validity states by row. This is essential for backward compatibility with
   * existing templates. When a dropdown's _invalid() state changes (e.g., when "Other" is selected but text field is
   * empty), this signal updates, which triggers the template to re-evaluate row error states.
   */
  protected readonly dropdownValidityByRow = computed(() => {
    const dropdowns = this.dropdowns();
    const result: Record<string, boolean> = {};

    for (const dropdown of dropdowns) {
      const idVal = dropdown.id();
      if (!idVal || !idVal.includes('::')) continue;

      const parts = idVal.split('::');
      const rowId = parts[0];
      const colId = parts.slice(1).join('::');

      // Skip if rowId is undefined
      if (!rowId || !colId) continue;

      // Check if this cell is enabled
      if (!this.isCellEnabled(rowId, colId)) continue;

      // If any dropdown in this row is invalid, mark the row as having dropdown errors
      if (dropdown._invalid()) {
        result[rowId] = true;
      } else if (result[rowId] === undefined) {
        result[rowId] = false;
      }
    }

    return result;
  });

  /**
   * Computed signal that tracks which rows have dropdowns that are both touched AND invalid. This is used to show row
   * errors immediately when "Other" is selected from a dropdown, without waiting for the entire table grid to be
   * touched.
   */
  protected readonly dropdownTouchedInvalidByRow = computed(() => {
    const dropdowns = this.dropdowns();
    const result: Record<string, boolean> = {};

    for (const dropdown of dropdowns) {
      const idVal = dropdown.id();
      if (!idVal || !idVal.includes('::')) continue;

      const parts = idVal.split('::');
      const rowId = parts[0];
      const colId = parts.slice(1).join('::');

      // Skip if rowId is undefined
      if (!rowId || !colId) continue;

      // Check if this cell is enabled
      if (!this.isCellEnabled(rowId, colId)) continue;

      // If any dropdown in this row is both touched AND invalid, mark the row
      // This enables immediate red background when "Other" is selected
      if (dropdown._invalid() && dropdown._touched()) {
        result[rowId] = true;
      } else if (result[rowId] === undefined) {
        result[rowId] = false;
      }
    }

    return result;
  });

  private readonly watchDropdownValidityEffect = effect(() => {
    const dropdowns = this.dropdowns();
    const states = dropdowns.map((d) => d._invalid());

    untracked(() => {
      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.updateValueAndValidity({ emitEvent: true });
      }
    });
  });

  /** Row values: rowLinkId → columnId → CellDataOrValue (supports both nested { value, linkId } and direct value) */
  protected readonly rowValues = signal<Record<string, Record<string, CellDataOrValue>>>({});
  protected readonly isTouched = signal<boolean>(false);

  /** Computed cell states: rowLinkId → columnId → enabled */
  protected readonly cellStates = computed(() => {
    const values = this.rowValues();
    const cols = this.columns();
    const rows = this.rows();
    const states: Record<string, Record<string, boolean>> = {};

    for (const row of rows) {
      const rowVal = values[row.linkId] || {};
      states[row.linkId] = this.ruleEvaluator.evaluateRow(rowVal, cols);
    }

    return states;
  });

  /** Sorted columns by colIndex */
  protected readonly sortedColumns = computed(() => {
    return [...this.columns()].sort((a, b) => a.colIndex - b.colIndex);
  });

  // --- CONFIGURE COLUMNS STATE ---
  /** Set of column ids currently hidden by the user, keyed to localStorage by linkId/tableId. */
  protected readonly hiddenColumnIds = signal<Set<string>>(new Set());
  /** Drag-resized column widths keyed by column id, persisted alongside hidden columns when `saveColumnWidths` is true. */
  protected readonly columnWidthOverrides = signal<Record<string, string>>({});
  protected readonly showColumnConfig = signal<boolean>(false);
  protected readonly columnConfigPosition = signal<{ top: number; left: number }>({ top: 0, left: 0 });

  /** Runtime override set via the "Configure Columns" toggle; null means fall back to the `freezeHeader` input default. */
  protected readonly freezeHeaderOverride = signal<boolean | null>(null);

  /** Effective frozen state: the user's override if set, otherwise the designer-configured default. */
  protected readonly headerFrozen = computed(() => this.freezeHeaderOverride() ?? this.freezeHeader());

  /** Sorted columns filtered down to only the ones the user has chosen to keep visible. */
  protected readonly visibleColumns = computed(() => {
    const hidden = this.hiddenColumnIds();
    const cols = this.sortedColumns();
    if (hidden.size === 0) return cols;
    return cols.filter((col) => !hidden.has(col.id));
  });

  /** True when the user has hidden at least one column (drives the "configured" indicator). */
  protected readonly hasCustomColumnConfig = computed(() => this.hiddenColumnIds().size > 0);

  /** Which existing header/body cell hosts the "Configure Columns" trigger (no dedicated column is added). */
  protected readonly configureColumnsHost = computed<'serial' | 'questionLabel'>(() =>
    this.showSerialNumber() ? 'serial' : 'questionLabel'
  );

  /** Serial-number column width, floored so the "Configure Columns" button never gets clipped when hosted there. */
  protected readonly effectiveSerialNumberWidth = computed(() =>
    this.showConfigureColumns() && this.configureColumnsHost() === 'serial' ? 'max(50px, 65px)' : '50px'
  );

  /** Get effective column width based on saved override, or the column's configured width (undefined = auto/1fr). */
  getColumnWidth(col: EnhancedColumnDefinition): string | undefined {
    const override = this.columnWidthOverrides()[col.id] || col.width;
    if (override) return override;

    // Reserve a sane minimum per input type so a growing column count compresses less-critical columns first
    // instead of shrinking a cell below its editor's usable width (e.g. a date picker bleeding into the next cell).
    // `minmax(px, 1fr)` keeps the column growable — a fixed px value here would drop it out of the table's flexible
    // 1fr distribution and leave unused space when there aren't enough other flexible columns to fill the row.
    switch (col.inputType) {
      case 'date':
        return 'minmax(160px, 1fr)';
      case 'text':
        return 'minmax(150px, 1fr)';
      case 'dropdown':
        return 'minmax(140px, 1fr)';
      default:
        return undefined;
    }
  }

  private _columnConfigStorageKey(): string {
    const key = this.linkId() || this.tableId();
    return `symphony.conditionalTableGrid.${key}.hiddenColumns`;
  }

  /**
   * Reads persisted column config. Backward compatible with the legacy format (a plain array of hidden column ids) as
   * well as the current object format (`{hiddenColumnIds, columnWidths}`).
   */
  private _loadHiddenColumnIds(): Set<string> {
    if (typeof localStorage === 'undefined') return new Set();
    try {
      const raw = localStorage.getItem(this._columnConfigStorageKey());
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return new Set(parsed.filter((id): id is string => typeof id === 'string'));
      }
      const hiddenColumnIds = parsed?.hiddenColumnIds;
      if (this.saveColumnWidths() && parsed?.columnWidths && typeof parsed.columnWidths === 'object') {
        this.columnWidthOverrides.set(parsed.columnWidths);
      }
      if (typeof parsed?.freezeHeader === 'boolean') {
        this.freezeHeaderOverride.set(parsed.freezeHeader);
      }
      return new Set(
        Array.isArray(hiddenColumnIds) ? hiddenColumnIds.filter((id): id is string => typeof id === 'string') : []
      );
    } catch {
      return new Set();
    }
  }

  private _persistColumnConfigState(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(
        this._columnConfigStorageKey(),
        JSON.stringify({
          hiddenColumnIds: Array.from(this.hiddenColumnIds()),
          columnWidths: this.saveColumnWidths() ? this.columnWidthOverrides() : {},
          freezeHeader: this.headerFrozen(),
        })
      );
    } catch {
      // Ignore storage failures (e.g. private browsing quota errors)
    }
  }

  /** Opens/closes the "Configure Columns" popover, positioning it under the given trigger element. */
  toggleColumnConfig(anchor: HTMLElement): void {
    if (this.showColumnConfig()) {
      this.showColumnConfig.set(false);
      return;
    }
    const rect = anchor.getBoundingClientRect();
    this.columnConfigPosition.set({ top: rect.bottom + 4, left: Math.max(0, rect.right - 240) });
    this.showColumnConfig.set(true);
  }

  @HostListener('document:click')
  closeColumnConfig(): void {
    this.showColumnConfig.set(false);
  }

  isColumnHidden(colId: string): boolean {
    return this.hiddenColumnIds().has(colId);
  }

  toggleColumnVisibility(colId: string): void {
    const hidden = new Set(this.hiddenColumnIds());
    if (hidden.has(colId)) {
      hidden.delete(colId);
    } else {
      hidden.add(colId);
    }
    this.hiddenColumnIds.set(hidden);
    this._persistColumnConfigState();
  }

  /** Toggles the pinned-header behaviour and persists the choice alongside the rest of the column config. */
  toggleFreezeHeader(): void {
    this.freezeHeaderOverride.set(!this.headerFrozen());
    this._persistColumnConfigState();
  }

  resetColumnConfig(): void {
    this.hiddenColumnIds.set(new Set());
    this.columnWidthOverrides.set({});
    this.freezeHeaderOverride.set(null);
    this._persistColumnConfigState();
  }

  /** Maps the grid widths reported after a drag-resize back onto data column ids, then persists them. */
  protected onColumnsResized(widths: string[]): void {
    if (!this.saveColumnWidths()) return;
    const leading = this.showSerialNumber() ? 2 : 1; // serial (optional) + question label
    const dataWidths = widths.slice(leading);
    const visible = this.visibleColumns();
    const next = { ...this.columnWidthOverrides() };

    visible.forEach((col, index) => {
      const width = dataWidths[index];
      if (width) {
        next[col.id] = width;
      }
    });

    this.columnWidthOverrides.set(next);
    this._persistColumnConfigState();
  }

  /**
   * Computed map of select all states per column. Only computed for eligible columns (checkbox or radio with single
   * option). Value is true if all enabled rows in that column are selected.
   */
  protected readonly selectAllStates = computed(() => {
    if (!this.selectAll()) {
      return {};
    }

    const states: Record<string, boolean> = {};
    const values = this.rowValues();
    const cellStates = this.cellStates();
    const cols = this.sortedColumns();

    for (const col of cols) {
      if (!this.isColumnSelectAllEligible(col)) {
        continue;
      }

      // Get all enabled rows for this column
      const enabledRows = this.rows().filter((row) => this.isCellEnabled(row.linkId, col.id));

      if (enabledRows.length === 0) {
        states[col.id] = false;
        continue;
      }

      // Determine what "selected" means for this column
      const expectedSelectedValue = this.getExpectedSelectedValueForColumn(col);

      // Check if all enabled rows are selected for this column
      const allSelected = enabledRows.every((row) => {
        const value = values[row.linkId]?.[col.id];

        // For checkbox: true means selected
        if (col.inputType === 'checkbox') {
          return value === true || (Array.isArray(value) && value.length > 0);
        }

        // For radio with one option: the option value means selected
        if (col.inputType === 'radio') {
          return value === expectedSelectedValue;
        }

        return false;
      });

      states[col.id] = allSelected;
    }

    return states;
  });

  protected shouldShowSelectAllForColumn(column: EnhancedColumnDefinition): boolean {
    if (!this.selectAll() || !this.isColumnSelectAllEligible(column)) {
      return false;
    }

    const selectedColumnIds = this.selectAllColumnIds();
    if (selectedColumnIds.length === 0) {
      return true;
    }

    return selectedColumnIds.includes(column.id);
  }

  /**
   * Effect to reinitialize rowValues from initialResponses when the input changes after mount. This handles the case
   * where the QuestionnaireResponse is applied after the component is already rendered (e.g., Save & Continue →
   * readonly/summary mode): initialResponses arrives late and ngOnInit has already fired with empty values.
   */
  private readonly syncInitialResponsesEffect = effect(() => {
    const initial = this.initialResponses();
    if (!initial || Object.keys(initial).length === 0) {
      return;
    }

    // Use untracked to read rowValues without creating a reactive dependency, preventing loops.
    const current = untracked(() => this.rowValues());
    if (Object.keys(current).length === 0) {
      this.rowValues.set(this.recalculateCalculatedDates({ ...initial }));
    }
  });

  private readonly otherInputControls: Record<string, FormControl<string>> = {};
  private readonly dateCellControls: Record<string, FormControl<DateSelection>> = {};
  /** Last source/offset signature a calculated-date cell was computed from, keyed by `rowId::columnId`. */
  private readonly calculatedDateSignatures = new Map<string, string>();

  constructor(@Self() @Optional() public ngControl: NgControl) {
    super();
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    // Drop any stale "Configure Columns" freeze-header override once the designer-configured default changes,
    // so properties-panel edits are reflected immediately instead of being masked by a persisted end-user choice.
    effect(() => {
      this.freezeHeader();
      untracked(() => {
        if (this.freezeHeaderOverride() !== null) {
          this.freezeHeaderOverride.set(null);
          this._persistColumnConfigState();
        }
      });
    });

    // Sync otherInputControls when rowValues changes programmatically
    effect(() => {
      const currentValues = this.rowValues();
      for (const [key, control] of Object.entries(this.otherInputControls)) {
        const [rowId, colId] = key.split('::');
        if (!rowId || !colId) continue;
        const text = this.getCellOtherText(rowId, colId);
        if (control.value !== text) {
          control.setValue(text, { emitEvent: false });
        }
      }
    });

    // Sync dateCellControls when rowValues changes programmatically (e.g. calculated-date recomputation, or a
    // restored/late-arriving QuestionnaireResponse). Without this, the per-cell FormControl bound to <hx-datepicker>
    // never learns about a newly-calculated value and the input visually stays empty even though rowValues (and the
    // emitted response) already hold the correct date.
    effect(() => {
      const currentValues = this.rowValues();
      const cols = this.columns();
      for (const [key, control] of Object.entries(this.dateCellControls)) {
        const [rowId, colId] = key.split('::');
        if (!rowId || !colId) continue;
        const column = cols.find((c) => c.id === colId);
        if (!column) continue;

        const nextSelection = this.toDateSelection(this.getDateCellInputValue(rowId, column), column);
        if (!this.dateSelectionsEqual(control.value, nextSelection)) {
          control.setValue(nextSelection, { emitEvent: false });
        }
      }
    });
  }

  /** Compares two DateSelection values (Date | DateRange | null) by their effective date(s), not by reference. */
  private dateSelectionsEqual(a: DateSelection, b: DateSelection): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
    if (a instanceof Date || b instanceof Date) return false;

    const startEqual = (a.start?.getTime() ?? null) === (b.start?.getTime() ?? null);
    const endEqual = (a.end?.getTime() ?? null) === (b.end?.getTime() ?? null);
    return startEqual && endEqual;
  }

  /** Effect to clear values of disabled cells */
  private readonly clearDisabledCellsEffect = effect(() => {
    const states = this.cellStates();
    const currentValues = this.rowValues();
    let hasChanges = false;
    const updatedValues: Record<string, Record<string, CellDataOrValue>> = { ...currentValues };

    // Clear values for all disabled cells
    for (const rowId in states) {
      const rowStates = states[rowId];
      const rowData = currentValues[rowId];
      if (!rowData) continue;

      for (const colId in rowStates) {
        const isEnabled = this.isCellEnabled(rowId, colId);
        const hasValue = rowData[colId] != null;

        // If cell is disabled and has a value, clear it
        if (!isEnabled && hasValue) {
          if (!updatedValues[rowId]) {
            updatedValues[rowId] = { ...rowData };
          }
          updatedValues[rowId][colId] = null;
          hasChanges = true;
        }
      }
    }

    // Update rowValues if any changes were made
    if (hasChanges) {
      this.rowValues.set(updatedValues);
      this.emitResponse();
    }
  });

  /** Previous enabled state per "rowId::colId", used to detect cells newly enabled by the rule engine. */
  private readonly _prevCellEnabled: Record<string, boolean> = {};

  /** Cell keys ("rowId::colId") that were disabled and just became enabled via the rule engine. */
  protected readonly newlyEnabledCellIds = signal<Set<string>>(new Set());

  /** Effect to flag a cell the moment it becomes enabled via the rule engine (was previously disabled). */
  private readonly touchNewlyEnabledCellsEffect = effect(() => {
    const rows = this.rows();
    const cols = this.columns();
    const current = untracked(() => this.newlyEnabledCellIds());
    let next: Set<string> | null = null;

    for (const row of rows) {
      for (const col of cols) {
        const key = `${row.linkId}::${col.id}`;
        const enabledNow = this.isCellEnabled(row.linkId, col.id);
        const wasEnabled = this._prevCellEnabled[key];

        if (wasEnabled === false && enabledNow && !current.has(key)) {
          next ??= new Set(current);
          next.add(key);
        }

        this._prevCellEnabled[key] = enabledNow;
      }
    }

    if (next) {
      this.newlyEnabledCellIds.set(next);
    }
  });

  /**
   * Whether a cell was just enabled by the rule engine (used to force required/error display, independent of
   * requiredMode).
   */
  protected isCellRequired(column: EnhancedColumnDefinition, row: EnhancedRowData): boolean {
    return this.isCellEnabled(row.linkId, column.id) && this.newlyEnabledCellIds().has(`${row.linkId}::${column.id}`);
  }

  /** Whether a cell should show its error state right now: it was just enabled by the rule engine and is still empty. */
  protected isCellHasError(column: EnhancedColumnDefinition, row: EnhancedRowData): boolean {
    if (!this.isCellRequired(column, row)) return false;

    const cellValue = this.getCellValue(row.linkId, column.id);
    return this.isCellValueEmpty(cellValue, column.inputType);
  }

  private _boundValidateFn: ValidatorFn | null = null;

  ngOnInit(): void {
    super.ngOnInit(); // Call base class initialization first

    if (this.ngControl && this.ngControl.control) {
      this._boundValidateFn = this.validate.bind(this);
      this.ngControl.control.addValidators(this._boundValidateFn);
      this.ngControl.control.updateValueAndValidity();
    }
    this.initializeFromResponses();
    this.hiddenColumnIds.set(this._loadHiddenColumnIds());
  }

  ngOnDestroy(): void {
    // Clean up effects to prevent memory leaks
    if (this.ngControl?.control && this._boundValidateFn) {
      this.ngControl.control.removeValidators(this._boundValidateFn);
      this.ngControl.control.updateValueAndValidity();
    }
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent) {
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (!this._elementRef.nativeElement.contains(relatedTarget)) {
      this.markAsTouched();
    }
  }

  // --- Public API ---

  /** Get current value of a specific cell. */
  getCellValue(rowId: string, colId: string): CellValue {
    const cellData = this.rowValues()[rowId]?.[colId];

    // Handle nested structure { value, linkId } or direct value for backward compatibility
    if (cellData && typeof cellData === 'object' && 'value' in cellData) {
      return (cellData as { value: CellValue }).value;
    }

    return (cellData as CellValue) ?? null;
  }

  /** Clear all cell values and reset states. */
  reset(): void {
    this.rowValues.set({});
    this.calculatedDateSignatures.clear();
    this.emitResponse();
  }

  // --- Cell Value Handling ---

  protected onCellValueChange(rowId: string, columnId: string, value: CellValue, otherText?: string): void {
    const current = this.rowValues();
    const rowData = { ...(current[rowId] || {}) };

    // Find the column definition to get its linkId
    const column = this.columns().find((col) => col.id === columnId);
    const columnLinkId = column?.linkId || columnId;

    // Store in nested structure { value, linkId, otherText }
    rowData[columnId] = { value, linkId: columnLinkId, ...(otherText !== undefined ? { otherText } : {}) };

    this.rowValues.set(this.recalculateCalculatedDates({ ...current, [rowId]: rowData }));
    this.emitResponse();
  }

  protected getDateCellFormControl(rowId: string, column: EnhancedColumnDefinition): FormControl<DateSelection> {
    const key = `${rowId}::${column.id}`;
    if (!this.dateCellControls[key]) {
      const control = new FormControl<DateSelection>(
        this.toDateSelection(this.getDateCellInputValue(rowId, column), column)
      );
      control.valueChanges.subscribe((selection) => this.onDateSelectionChange(rowId, column, selection));
      this.dateCellControls[key] = control;
    }
    return this.dateCellControls[key];
  }

  /** True when a calculated date cannot currently be derived from its configured source/offset values. */
  protected shouldUseCalculatedDateFallback(rowId: string, column: EnhancedColumnDefinition): boolean {
    return this.isCalculatedDateColumn(column) && !this.hasCalculationInputs(rowId, column);
  }

  protected getCalculatedDateFallbackInputType(column: EnhancedColumnDefinition): 'date' | 'text' {
    return column.dateConfig?.calculatedFallbackInputType ?? 'date';
  }

  protected getCalculatedDateFallbackValue(rowId: string, column: EnhancedColumnDefinition): string {
    // Once calculation inputs are missing, always resolve to the configured default — do not fall through to a
    // stale display value left over from before the source/offset was cleared.
    if (!this.hasCalculationInputs(rowId, column)) {
      return column.dateConfig?.calculatedFallbackValue ?? '';
    }
    const value = this.getCellDisplayValue(rowId, column.id);
    return typeof value === 'string' ? value : (column.dateConfig?.calculatedFallbackValue ?? '');
  }

  protected getDateMode(column: EnhancedColumnDefinition): 'single' | 'range' {
    return column.dateConfig?.mode ?? 'single';
  }

  protected getDateDisplayFormat(column: EnhancedColumnDefinition): string {
    return column.dateConfig?.displayFormat || 'MM/DD/YYYY';
  }

  protected getDateBoundary(column: EnhancedColumnDefinition, boundary: 'min' | 'max'): Date | null {
    const value = column.dateConfig?.[boundary];
    if (!value) return null;
    return this.dateAdapter.fromIso8601(value) ?? this.dateAdapter.parse(value, [this.getDateDisplayFormat(column)]);
  }

  protected isCalculatedDateColumn(column: EnhancedColumnDefinition): boolean {
    return column.inputType === 'date' && column.dateConfig?.role === 'calculated';
  }

  private getDateCellInputValue(rowId: string, column: EnhancedColumnDefinition): CellValue {
    const value = this.getCellValue(rowId, column.id);
    if (value !== null && value !== undefined) return value;
    if (this.shouldUseCalculatedDateFallback(rowId, column)) {
      return column.dateConfig?.calculatedFallbackValue ?? null;
    }
    return null;
  }

  private hasCalculationInputs(rowId: string, targetColumn: EnhancedColumnDefinition): boolean {
    const calculation = targetColumn.dateConfig?.calculation;
    if (!calculation) return false;

    const sourceColumn = this.columns().find((column) => column.id === calculation.sourceColumnId);
    if (!sourceColumn) return false;

    const sourceValue = this.getCellValueFromRecord(this.rowValues(), rowId, sourceColumn.id);
    const baseDate = this.getCalculationBaseDate(sourceValue, sourceColumn, calculation.rangeEndpoint);
    const offsets = this.getCalculationOffsets(this.rowValues(), rowId, calculation);
    return !!baseDate && offsets.start !== null;
  }

  private onDateSelectionChange(rowId: string, column: EnhancedColumnDefinition, selection: DateSelection): void {
    const current = this.rowValues();
    const rowData = { ...(current[rowId] || {}) };
    const cell = rowData[column.id];
    const existing = cell && typeof cell === 'object' && 'value' in cell ? cell : {};
    rowData[column.id] = {
      ...existing,
      value: this.fromDateSelection(selection, column),
      linkId: column.linkId || column.id,
    } as CellDataOrValue;
    this.rowValues.set(this.recalculateCalculatedDates({ ...current, [rowId]: rowData }));
    this.emitResponse();
  }

  /**
   * Recomputes every calculated date column (source date + offset) for every row, but only overwrites a cell when its
   * source/offset inputs actually changed since the last time it was computed — otherwise a manual edit the user made
   * directly on the calculated cell is left alone. Also seeds read-only text cells with their configured `rowOptions`
   * label so the emitted response records the displayed text instead of staying null (the static `<span>` never fires a
   * change handler to write it itself).
   */
  private recalculateCalculatedDates(
    values: Record<string, Record<string, CellDataOrValue>>
  ): Record<string, Record<string, CellDataOrValue>> {
    const next = { ...values };

    for (const row of this.rows()) {
      const rowData = { ...(next[row.linkId] || {}) };
      let changed = false;

      for (const col of this.columns()) {
        if (col.inputType !== 'text' || !col.isReadOnly) continue;

        const existing = rowData[col.id];
        const existingValue =
          existing && typeof existing === 'object' && 'value' in existing ? (existing as CellData).value : existing;
        if (existingValue !== undefined && existingValue !== null) continue;

        const label = this.getRowOption(row.linkId, col.id)?.label;
        if (label === undefined) continue;

        rowData[col.id] = { value: label, linkId: col.linkId || col.id };
        changed = true;
      }

      for (const targetColumn of this.columns()) {
        const calculation = targetColumn.dateConfig?.calculation;
        if (!this.isCalculatedDateColumn(targetColumn) || !calculation) continue;

        const sourceColumn = this.columns().find((column) => column.id === calculation.sourceColumnId);
        if (!sourceColumn) continue;

        const sourceValue = this.getCellValueFromRecord(next, row.linkId, sourceColumn.id);
        const baseDate = this.getCalculationBaseDate(sourceValue, sourceColumn, calculation.rangeEndpoint);
        const offsets = this.getCalculationOffsets(next, row.linkId, calculation);
        const inputsComplete = !!baseDate && offsets.start !== null;
        const signedOffsetStart = inputsComplete
          ? calculation.operation === 'subtract'
            ? -offsets.start!
            : offsets.start!
          : 0;
        const signedOffsetEnd = inputsComplete
          ? calculation.operation === 'subtract'
            ? -(offsets.end ?? offsets.start)!
            : (offsets.end ?? offsets.start)!
          : 0;

        // Identifies the exact inputs this cell was last computed from. Unchanged since the prior pass means the
        // source/offset haven't moved, so any value sitting in the cell right now — including a manual edit made
        // directly on the calculated cell — is left untouched instead of being stomped on every recalculation.
        const signature = inputsComplete
          ? `${baseDate!.toISOString()}|${signedOffsetStart}|${signedOffsetEnd}|${calculation.unit}`
          : 'missing';
        const signatureKey = `${row.linkId}::${targetColumn.id}`;
        if (this.calculatedDateSignatures.get(signatureKey) === signature) continue;
        this.calculatedDateSignatures.set(signatureKey, signature);

        if (!inputsComplete) {
          const fallbackValue = targetColumn.dateConfig?.calculatedFallbackValue;
          if (fallbackValue) {
            const existingCell = rowData[targetColumn.id];
            const existingValue: CellValue =
              existingCell &&
              typeof existingCell === 'object' &&
              !Array.isArray(existingCell) &&
              'value' in existingCell
                ? (existingCell as CellData).value
                : ((existingCell as CellValue | undefined) ?? null);

            // The UI always shows this static default once calculation inputs are incomplete (see
            // getCalculatedDateFallbackValue), so record it in the response instead of leaving the cell empty.
            if (existingValue !== fallbackValue) {
              rowData[targetColumn.id] = { value: fallbackValue, linkId: targetColumn.linkId || targetColumn.id };
              changed = true;
            }
          } else if (targetColumn.id in rowData) {
            // A calculated-role cell never holds a genuine independent answer, so clear it unconditionally once its
            // inputs are missing so the configured fallback reliably reappears.
            delete rowData[targetColumn.id];
            changed = true;
          }
          continue;
        }

        const calculatedValue = this.buildCalculatedDateValue(
          sourceValue,
          sourceColumn,
          targetColumn,
          calculation.rangeEndpoint,
          signedOffsetStart,
          signedOffsetEnd,
          calculation.unit
        );
        if (calculatedValue === null) {
          if (targetColumn.id in rowData) {
            delete rowData[targetColumn.id];
            changed = true;
          }
          continue;
        }

        const existingCell = rowData[targetColumn.id];
        const existingValue: CellValue =
          existingCell && typeof existingCell === 'object' && !Array.isArray(existingCell) && 'value' in existingCell
            ? (existingCell as CellData).value
            : ((existingCell as CellValue | undefined) ?? null);

        // Skip the write when nothing actually changed to avoid needless re-emissions on every keystroke elsewhere.
        if (this.cellValuesEqual(existingValue, calculatedValue)) continue;

        rowData[targetColumn.id] = {
          value: calculatedValue,
          linkId: targetColumn.linkId || targetColumn.id,
        } as CellDataOrValue;
        changed = true;
      }

      if (changed) next[row.linkId] = rowData;
    }

    return next;
  }

  /**
   * Computes the calculated date value shaped for the target column's own mode (single formatted-date string, or a
   * `start-end` formatted-date string for a range) so a calculated column always renders correctly regardless of
   * whether its source is a single date or a date range. A range target derives its start bound from
   * `signedOffsetStart` and its end bound from `signedOffsetEnd` independently (they're equal when no end offset was
   * configured, reproducing the legacy mirrored behavior). When the target is single, only `signedOffsetStart` is ever
   * used, and the configured range endpoint (start/end) picks the base date if the source itself is a range.
   */
  private buildCalculatedDateValue(
    sourceValue: CellValue,
    sourceColumn: EnhancedColumnDefinition,
    targetColumn: EnhancedColumnDefinition,
    rangeEndpoint: 'start' | 'end' | undefined,
    signedOffsetStart: number,
    signedOffsetEnd: number,
    unit: DateCalculationUnit
  ): CellValue | null {
    const sourceSelection = this.toDateSelection(sourceValue, sourceColumn);
    if (!sourceSelection) return null;

    // Format using the target column's own displayFormat (not ISO) so a calculated result matches the format the
    // respondent sees/entered elsewhere in the grid.
    const targetFormat = this.getDateDisplayFormat(targetColumn);
    const offsetDate = (date: Date | null, offset: number): Date | null =>
      date ? this.applyDateOffset(date, offset, unit) : null;
    const iso = (date: Date | null): string | null => (date ? this.dateAdapter.format(date, targetFormat) : null);

    if (targetColumn.dateConfig?.mode === 'range') {
      let startDate: Date | null;
      let endDate: Date | null;
      if (sourceSelection instanceof Date) {
        startDate = offsetDate(sourceSelection, signedOffsetStart);
        endDate = offsetDate(sourceSelection, signedOffsetEnd);
      } else {
        startDate = offsetDate(sourceSelection.start, signedOffsetStart);
        endDate = offsetDate(sourceSelection.end, signedOffsetEnd);
      }

      // A subtract operation with a larger end-offset lands on an earlier date than the start-offset, which would
      // otherwise invert the range — always surface the chronologically earlier date as the start.
      if (startDate && endDate && startDate.getTime() > endDate.getTime()) {
        [startDate, endDate] = [endDate, startDate];
      }

      const startIso = iso(startDate);
      const endIso = iso(endDate);
      if (startIso && endIso) return `${startIso} - ${endIso}`;
      return startIso ?? endIso ?? null;
    }

    const baseDate =
      sourceSelection instanceof Date ? sourceSelection : rangeEndpoint ? sourceSelection[rangeEndpoint] : null;
    if (!baseDate) return null;
    return iso(offsetDate(baseDate, signedOffsetStart));
  }

  /** Compares cell values that may be a plain ISO string or a `{start, end}` range shape. */
  private cellValuesEqual(a: CellValue, b: CellValue): boolean {
    if (a === b) return true;
    if (a && b && typeof a === 'object' && typeof b === 'object' && 'start' in a && 'start' in b) {
      const rangeA = a as { start: string | null; end: string | null };
      const rangeB = b as { start: string | null; end: string | null };
      return rangeA.start === rangeB.start && rangeA.end === rangeB.end;
    }
    return false;
  }

  private getCalculationBaseDate(
    value: CellValue,
    column: EnhancedColumnDefinition,
    endpoint?: 'start' | 'end'
  ): Date | null {
    const selection = this.toDateSelection(value, column);
    if (selection instanceof Date) return selection;
    if (!selection || !endpoint) return null;
    return selection[endpoint];
  }

  /**
   * Resolves the start/end offset pair driving a calculated date, from either a fixed designer value or a read-only
   * frequency column. `end` falls back to `start` when no range-mode end value was configured, which reproduces the
   * legacy single-offset (mirrored-range) behavior exactly.
   */
  private getCalculationOffsets(
    values: Record<string, Record<string, CellDataOrValue>>,
    rowId: string,
    calculation: DateCalculationConfig
  ): { start: number | null; end: number | null } {
    const toFinite = (value: unknown): number | null => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };

    let start: number | null;
    let end: number | null;

    if (calculation.offsetSource === 'fixed') {
      start = toFinite(calculation.fixedValue);
      end = calculation.fixedEndValue !== undefined ? toFinite(calculation.fixedEndValue) : start;
    } else {
      const columnId = calculation.offsetColumnId;
      if (!columnId) return { start: null, end: null };

      // A configured rowOptions value is the authoritative numeric driver for this offset — it must win over
      // whatever's in the response, since a read-only offset column's stored value is just the display label.
      const rowOptionValue = this.getRowOptionValue(rowId, columnId);
      const rawStart =
        rowOptionValue !== undefined ? rowOptionValue : this.getCellValueFromRecord(values, rowId, columnId);
      start = toFinite(rawStart);

      const rowOptionEndValue = this.getRowOptionEndValue(rowId, columnId);
      end = rowOptionEndValue !== undefined ? toFinite(rowOptionEndValue) : start;
    }

    // A configured end smaller than start is a misconfiguration; mirror start defensively rather than invert.
    if (start !== null && end !== null && end < start) end = start;

    return { start, end };
  }

  private applyDateOffset(date: Date, offset: number, unit: DateCalculationUnit): Date {
    switch (unit) {
      case 'days':
        return this.dateAdapter.addCalendarDays(date, offset);
      case 'weeks':
        return this.dateAdapter.addCalendarDays(date, offset * 7);
      case 'months':
        return this.dateAdapter.addCalendarMonths(date, offset);
      case 'years':
        return this.dateAdapter.addCalendarYears(date, offset);
    }
  }

  /**
   * Reads a cell's value, falling back to its design-time default (e.g. a read-only column's per-row `rowOptions` value
   * configured in the question JSON) when the respondent hasn't produced a rowValues entry for it yet.
   */
  private getCellValueFromRecord(
    values: Record<string, Record<string, CellDataOrValue>>,
    rowId: string,
    columnId: string
  ): CellValue {
    const cell = values[rowId]?.[columnId];
    if (cell && typeof cell === 'object' && 'value' in cell) return cell.value;
    if (cell !== undefined && cell !== null) return cell as CellValue;

    const rowOptionValue = this.getRowOptionValue(rowId, columnId);
    if (rowOptionValue !== undefined) return rowOptionValue;

    return (this.cellControls()[rowId]?.[columnId]?.value ?? null) as CellValue;
  }

  /**
   * The single `{value, label}` pair configured for a column on a specific row (`rows[].rowOptions`), used to decouple
   * a read-only column's displayed label from the actual value calculations read.
   */
  private getRowOption(rowId: string, columnId: string): InputOption | undefined {
    return this.rows().find((row) => row.linkId === rowId)?.rowOptions?.[columnId]?.[0];
  }

  private getRowOptionValue(rowId: string, columnId: string): CellValue | undefined {
    const option = this.getRowOption(rowId, columnId);
    return option ? (option.value as CellValue) : undefined;
  }

  private getRowOptionEndValue(rowId: string, columnId: string): CellValue | undefined {
    const option = this.getRowOption(rowId, columnId);
    return option?.rangeEndValue as CellValue | undefined;
  }

  /** Display text for a read-only text cell: prefers the row-configured label over the raw underlying value. */
  protected getReadOnlyTextDisplay(rowId: string, columnId: string): string {
    const label = this.getRowOption(rowId, columnId)?.label;
    if (label) return label;
    const value = this.getCellDisplayValue(rowId, columnId);
    return value?.toString() ?? '';
  }

  private toDateSelection(value: CellValue, column: EnhancedColumnDefinition): DateSelection {
    if (typeof value === 'string') {
      if (column.dateConfig?.mode === 'range') {
        return this.toDateRangeSelection(value, column);
      }
      return this.toDateValue(value, column);
    }
    if (value && typeof value === 'object' && 'start' in value && 'end' in value) {
      const range = value as { start: string | null; end: string | null };
      return {
        start: range.start ? this.toDateValue(range.start, column) : null,
        end: range.end ? this.toDateValue(range.end, column) : null,
      } as DateRange;
    }
    return null;
  }

  /** Converts a persisted ISO or display-formatted date to the picker value type. */
  private toDateValue(value: string, column: EnhancedColumnDefinition): Date | null {
    return this.dateAdapter.fromIso8601(value) ?? this.dateAdapter.parse(value, [this.getDateDisplayFormat(column)]);
  }

  /**
   * Converts legacy string defaults such as `01/01/2000-01/01/2000` to the range shape expected by HxDatePicker.
   * Testing each dash position also handles ISO dates, whose internal hyphens must not be mistaken for the separator.
   */
  private toDateRangeSelection(value: string, column: EnhancedColumnDefinition): DateRange | null {
    const singleDate = this.toDateValue(value, column);
    if (singleDate) {
      return { start: singleDate, end: null };
    }

    for (let index = 0; index < value.length; index++) {
      if (!['-', '\u2013', '\u2014'].includes(value.charAt(index))) continue;

      const start = this.toDateValue(value.slice(0, index).trim(), column);
      const end = this.toDateValue(value.slice(index + 1).trim(), column);
      if (start && end) {
        return { start, end };
      }
    }

    return null;
  }

  /**
   * Serializes the picker's selection using the column's configured `displayFormat` (not ISO) so the stored/emitted
   * response value matches what the respondent actually entered. A range is stored as a single `start - end` string
   * (not a `{start,end}` object) — `toDateRangeSelection` already parses that shape back into a range.
   */
  private fromDateSelection(selection: DateSelection, column: EnhancedColumnDefinition): CellValue {
    if (!selection) return null;
    const format = this.getDateDisplayFormat(column);
    if (selection instanceof Date) return this.dateAdapter.format(selection, format);

    const startText = selection.start ? this.dateAdapter.format(selection.start, format) : null;
    const endText = selection.end ? this.dateAdapter.format(selection.end, format) : null;
    if (startText && endText) return `${startText} - ${endText}`;
    return startText ?? endText ?? null;
  }

  protected onCheckboxChange(rowId: string, columnId: string, checked: boolean): void {
    this.onCellValueChange(rowId, columnId, checked);
  }

  protected onCheckboxValuesChange(rowId: string, columnId: string, selectedValues: (string | number)[]): void {
    this.onCellValueChange(rowId, columnId, selectedValues.length > 0);
  }

  protected onRadioChange(rowId: string, columnId: string, change: ControlChange<any>): void {
    const nextValue = this.resolveRadioChangeValue(change);

    if (!this.shouldApplyGlobalRadioExclusivity(columnId)) {
      this.onCellValueChange(rowId, columnId, nextValue);
      return;
    }

    const current = this.rowValues();
    const updated: Record<string, Record<string, CellDataOrValue>> = { ...current };

    // Enforce radio mutual exclusivity across radio columns within the same row.
    const rowData = { ...(current[rowId] || {}) };
    const radioColumns = this.columns().filter((column) => column.inputType === 'radio');
    for (const radioColumn of radioColumns) {
      const radioLinkId = radioColumn.linkId || radioColumn.id;
      rowData[radioColumn.id] = { value: null, linkId: radioLinkId };
    }

    // Set the selected radio value in nested structure
    const selectedColumn = this.columns().find((col) => col.id === columnId);
    const selectedLinkId = selectedColumn?.linkId || columnId;
    rowData[columnId] = { value: nextValue, linkId: selectedLinkId };
    updated[rowId] = rowData;

    this.rowValues.set(updated);
    this.emitResponse();
  }

  private resolveRadioChangeValue(change: any): CellValue {
    if (change && typeof change === 'object' && 'value' in change) {
      return change.value ?? null;
    }

    return change ?? null;
  }

  /**
   * Global radio exclusivity is only needed for yes/no-style marker columns where every radio column has exactly one
   * option with an empty label.
   */
  private shouldApplyGlobalRadioExclusivity(columnId: string): boolean {
    const targetColumn = this.columns().find((column) => column.id === columnId);
    if (!targetColumn || targetColumn.inputType !== 'radio') {
      return false;
    }

    const radioColumns = this.columns().filter((column) => column.inputType === 'radio');
    if (radioColumns.length === 0) {
      return false;
    }

    return radioColumns.every((column) => {
      const options = column.options ?? [];
      if (options.length !== 1) {
        return false;
      }

      const onlyOption = options[0];
      return (onlyOption?.label ?? '').trim() === '';
    });
  }

  protected onDropdownChange(rowId: string, columnId: string, value: any): void {
    const isOther = this.isOtherOptionSelected(rowId, columnId, value);
    const currentOtherText = isOther ? this.getCellOtherText(rowId, columnId) : '';
    if (!isOther) {
      // Clear the form control value if "Other" is deselected
      const key = `${rowId}::${columnId}`;
      if (this.otherInputControls[key]) {
        this.otherInputControls[key].setValue('', { emitEvent: false });
      }
    }
    this.onCellValueChange(rowId, columnId, value, currentOtherText || undefined);
  }

  protected getOtherInputControl(rowId: string, columnId: string): FormControl<string> {
    const key = `${rowId}::${columnId}`;
    let control = this.otherInputControls[key];
    if (!control) {
      const initialOtherText = this.getCellOtherText(rowId, columnId) || '';
      control = new FormControl<string>(initialOtherText, { nonNullable: true });

      control.valueChanges.subscribe((val) => {
        this.onCellOtherTextChange(rowId, columnId, val || '');
      });

      this.otherInputControls[key] = control;
    }
    return control;
  }

  protected onCellOtherTextChange(rowId: string, columnId: string, otherText: string): void {
    const currentVal = this.getCellValue(rowId, columnId);
    this.onCellValueChange(rowId, columnId, currentVal, otherText);
  }

  protected getCellOtherText(rowId: string, colId: string): string {
    const cellData = this.rowValues()[rowId]?.[colId];
    if (cellData && typeof cellData === 'object' && 'otherText' in cellData) {
      return (cellData as { otherText?: string }).otherText || '';
    }
    return '';
  }

  private isOtherOptionSelected(rowId: string, columnId: string, value: any): boolean {
    const column = this.columns().find((col) => col.id === columnId);
    if (!column) return false;
    const otherVal = this.getOtherOptionValue(column, rowId);
    if (Array.isArray(value)) {
      return value.includes(otherVal);
    }
    return value === otherVal;
  }

  protected isDropdownMultiple(column: EnhancedColumnDefinition, rowLinkId?: string): boolean {
    const cellProps = rowLinkId ? this.cellControls()[rowLinkId]?.[column.id] : undefined;
    if (typeof cellProps?.multiple === 'boolean') {
      return cellProps.multiple;
    }

    return false;
  }

  protected onTextChange(rowId: string, columnId: string, value: string): void {
    this.onCellValueChange(rowId, columnId, value);
  }

  protected onRatingChange(rowId: string, columnId: string, value: number | null): void {
    this.onCellValueChange(rowId, columnId, value);
  }

  protected getCellMode(rowId: string, columnId: string): CellMode {
    return this.cellSettings()[rowId]?.[columnId]?.mode ?? 'normal';
  }

  protected isCellEnabled(rowId: string, columnId: string): boolean {
    const mode = this.getCellMode(rowId, columnId);
    if (mode === 'empty' || mode === 'disabled') {
      return false;
    }
    return this.cellStates()[rowId]?.[columnId] !== false;
  }

  protected getCellDisplayValue(rowId: string, columnId: string): CellValue {
    const cellData = this.rowValues()[rowId]?.[columnId];

    // Handle nested structure { value, linkId } or direct value for backward compatibility
    if (cellData !== undefined && cellData !== null) {
      if (typeof cellData === 'object' && 'value' in cellData) {
        return (cellData as { value: CellValue }).value;
      }
      return cellData;
    }

    const cellDefault = this.cellControls()[rowId]?.[columnId]?.value;
    return (cellDefault ?? null) as CellValue;
  }

  protected getRatingValue(rowId: string, columnId: string): number {
    const cellData = this.rowValues()[rowId]?.[columnId];

    // Handle nested structure { value, linkId } or direct value for backward compatibility
    let val: unknown;
    if (cellData && typeof cellData === 'object' && 'value' in cellData) {
      val = (cellData as { value: unknown }).value;
    } else {
      val = cellData;
    }

    return typeof val === 'number' ? val : 0;
  }

  protected getRadioOptions(
    column: EnhancedColumnDefinition,
    rowLinkId?: string
  ): Array<{ id: string; label: string; value: string | number | boolean }> {
    // Prefer options from cell controls (source of truth from nested FHIR items)
    const cellProps = rowLinkId ? this.cellControls()[rowLinkId]?.[column.id] : undefined;
    if (cellProps?.options && cellProps.options.length > 0) {
      return cellProps.options.map((option, index) => ({
        id: `${column.id}-radio-opt-${index}`,
        label: option.label,
        value: option.value as string | number | boolean,
      }));
    }

    // Fallback to row-specific options then column options
    const rowSpecific = rowLinkId
      ? this.rows().find((r) => r.linkId === rowLinkId)?.rowOptions?.[column.id]
      : undefined;
    const opts = rowSpecific && rowSpecific.length > 0 ? rowSpecific : (column.options ?? []);
    if (opts.length > 0) {
      return opts.map((option, index) => ({
        id: `${column.id}-radio-opt-${index}`,
        label: option.label,
        value: option.value as string | number | boolean,
      }));
    }

    // Ensure radio type is visually reflected in preview even before options are configured.
    return [{ id: `${column.id}-radio-fallback`, label: '', value: '__selected__' }];
  }

  protected getDropdownOptions(
    column: EnhancedColumnDefinition,
    rowLinkId?: string
  ): Array<{ id: string; label: string; value: string | number | boolean }> {
    // Prefer options from cell controls (source of truth from nested FHIR items)
    const cellProps = rowLinkId ? this.cellControls()[rowLinkId]?.[column.id] : undefined;
    if (cellProps?.options && cellProps.options.length > 0) {
      return cellProps.options.map((option, index) => ({
        id: `${column.id}-opt-${index}`,
        label: option.label,
        value: option.value as string | number | boolean,
      }));
    }

    // Fallback to row-specific options then column options
    const rowSpecific = rowLinkId
      ? this.rows().find((r) => r.linkId === rowLinkId)?.rowOptions?.[column.id]
      : undefined;
    const opts = rowSpecific && rowSpecific.length > 0 ? rowSpecific : (column.options ?? []);
    return opts.map((option, index) => ({
      id: `${column.id}-opt-${index}`,
      label: option.label,
      value: option.value as string | number | boolean,
    }));
  }

  /** Enables the dropdown's "Other" text field when explicitly configured or when an "Other" option is present. */
  protected shouldShowOtherInput(column: EnhancedColumnDefinition, rowLinkId?: string): boolean {
    // Prefer cell control props
    const cellProps = rowLinkId ? this.cellControls()[rowLinkId]?.[column.id] : undefined;
    if (cellProps?.showOtherInput != null) {
      return cellProps.showOtherInput;
    }

    if (column.showOtherInput != null) {
      return column.showOtherInput;
    }

    // Determine from options
    const cellOptions = cellProps?.options;
    const rowSpecific = rowLinkId
      ? this.rows().find((r) => r.linkId === rowLinkId)?.rowOptions?.[column.id]
      : undefined;
    const opts =
      cellOptions && cellOptions.length > 0
        ? cellOptions
        : rowSpecific && rowSpecific.length > 0
          ? rowSpecific
          : (column.options ?? []);

    return opts.some((option) => isOtherOption(option));
  }

  /** Resolves the option value that represents "Other" for the current dropdown. */
  protected getOtherOptionValue(column: EnhancedColumnDefinition, rowLinkId?: string): string {
    // Prefer cell control props
    const cellProps = rowLinkId ? this.cellControls()[rowLinkId]?.[column.id] : undefined;
    if (cellProps?.otherValue) {
      return cellProps.otherValue;
    }

    if (column.otherValue) {
      return column.otherValue;
    }

    const cellOptions = cellProps?.options;
    const rowSpecific = rowLinkId
      ? this.rows().find((r) => r.linkId === rowLinkId)?.rowOptions?.[column.id]
      : undefined;
    const opts =
      cellOptions && cellOptions.length > 0
        ? cellOptions
        : rowSpecific && rowSpecific.length > 0
          ? rowSpecific
          : (column.options ?? []);

    const other = opts.find((option) => isOtherOption(option));

    return other?.value ?? 'other';
  }

  protected shouldShowTags(column: EnhancedColumnDefinition, rowLinkId?: string): boolean {
    // Prefer cell control props
    const cellProps = rowLinkId ? this.cellControls()[rowLinkId]?.[column.id] : undefined;
    if (typeof cellProps?.showTags === 'boolean') {
      return cellProps.showTags;
    }
    return false;
  }

  protected getRatingArray(scale: number): number[] {
    return Array.from({ length: scale || 5 }, (_, i) => i + 1);
  }

  /** Get a string property from cell controls map for template binding. */
  protected getCellControlProp(rowLinkId: string, colId: string, prop: keyof CellControlProps): string {
    const value = this.cellControls()[rowLinkId]?.[colId]?.[prop];
    return typeof value === 'string' ? value : '';
  }

  /**
   * Check if a column is eligible for select all functionality. Only checkbox and radio (with single option) columns
   * are eligible.
   */
  protected isColumnSelectAllEligible(column: EnhancedColumnDefinition): boolean {
    if (column.inputType === 'checkbox') {
      return true;
    }

    if (column.inputType === 'radio') {
      // Radio select all only works with single option
      const options = column.options ?? [];
      return options.length === 1;
    }

    return false;
  }

  /**
   * Get the expected value when a cell is "selected" for a column. For checkbox: true For radio with one option: the
   * option value
   */
  protected getExpectedSelectedValueForColumn(column: EnhancedColumnDefinition): CellValue {
    if (column.inputType === 'checkbox') {
      return true;
    }

    if (column.inputType === 'radio') {
      const options = column.options ?? [];
      if (options.length === 1 && options[0]) {
        return options[0].value;
      }
    }

    return null;
  }

  /** Check if select all should be disabled for a column. Disabled when no rows are enabled for that column. */
  protected isSelectAllDisabledForColumn(columnId: string): boolean {
    const enabledRows = this.rows().filter((row) => this.isCellEnabled(row.linkId, columnId));
    return enabledRows.length === 0;
  }

  /** Check if select all checkbox is currently checked for a column. */
  protected isSelectAllCheckedForColumn(columnId: string): boolean {
    return this.selectAllStates()[columnId] === true;
  }

  /** Toggle select all rows for a column. If all enabled rows are selected, deselect all; otherwise, select all. */
  protected toggleSelectAllForColumn(columnId: string): void {
    const cellStates = this.cellStates();
    const current = this.rowValues();
    const updated: Record<string, Record<string, CellDataOrValue>> = { ...current };
    const column = this.columns().find((c) => c.id === columnId);

    if (!column) {
      return;
    }

    // Get all enabled rows for this column
    const enabledRows = this.rows().filter((row) => this.isCellEnabled(row.linkId, columnId));

    if (enabledRows.length === 0) {
      return;
    }

    const selectedValue = this.getExpectedSelectedValueForColumn(column);

    // Check if all enabled rows are currently selected
    const allSelected = enabledRows.every((row) => {
      const cellData = current[row.linkId]?.[columnId];

      // Extract value from nested structure or use direct value for backward compatibility
      let value: CellValue;
      if (cellData && typeof cellData === 'object' && 'value' in cellData) {
        value = (cellData as { value: CellValue }).value;
      } else {
        value = (cellData ?? null) as CellValue;
      }

      // For checkbox: true means selected
      if (column.inputType === 'checkbox') {
        return value === true || (Array.isArray(value) && value.length > 0);
      }

      // For radio with one option: the option value means selected
      if (column.inputType === 'radio') {
        return value === selectedValue;
      }

      return false;
    });

    // Toggle: if all selected, deselect; otherwise, select all
    for (const row of enabledRows) {
      const rowData = updated[row.linkId] || { ...current[row.linkId] } || {};
      const columnLinkId = column.linkId || columnId;

      if (allSelected) {
        // Deselect all - clear the value using nested structure
        rowData[columnId] = { value: null, linkId: columnLinkId };

        // If this is a radio column and global exclusivity applies, clear other radios
        if (column.inputType === 'radio' && this.shouldApplyGlobalRadioExclusivity(columnId)) {
          const radioColumns = this.columns().filter((col) => col.inputType === 'radio');
          for (const radioColumn of radioColumns) {
            if (radioColumn.id === columnId) {
              const radioLinkId = radioColumn.linkId || radioColumn.id;
              rowData[radioColumn.id] = { value: null, linkId: radioLinkId };
            }
          }
        }
      } else {
        // Select all - set the selected value using nested structure
        // If this is a radio column and global exclusivity applies, clear other radios in this row
        if (column.inputType === 'radio' && this.shouldApplyGlobalRadioExclusivity(columnId)) {
          const radioColumns = this.columns().filter((col) => col.inputType === 'radio');
          for (const radioColumn of radioColumns) {
            const radioLinkId = radioColumn.linkId || radioColumn.id;
            rowData[radioColumn.id] = { value: null, linkId: radioLinkId };
          }
        }

        rowData[columnId] = { value: selectedValue, linkId: columnLinkId };
      }

      updated[row.linkId] = rowData;
    }

    this.rowValues.set(updated);
    this.emitResponse();
  }

  markAsTouched(): void {
    if (!this.isTouched()) {
      this.isTouched.set(true);
      if (this._onTouched) this._onTouched();
      // Also mark the form control as touched if it exists
      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.markAsTouched();
      }
    }
  }

  protected override _setValue(
    value:
      | Record<string, Record<string, CellDataOrValue>>
      | { responses?: Record<string, Record<string, CellDataOrValue>> }
      | null
      | undefined
  ): void {
    if (!value) {
      this.rowValues.set({});
      return;
    }

    const compound = value as { responses?: Record<string, Record<string, CellDataOrValue>> };
    if (compound.responses && typeof compound.responses === 'object') {
      this.rowValues.set(this.recalculateCalculatedDates({ ...compound.responses }));
      return;
    }

    this.rowValues.set(
      this.recalculateCalculatedDates({ ...(value as Record<string, Record<string, CellDataOrValue>>) })
    );
  }

  override writeValue(
    value:
      | Record<string, Record<string, CellDataOrValue>>
      | { responses?: Record<string, Record<string, CellDataOrValue>> }
      | null
  ): void {
    this._setValue(value);

    // Keep control validity in sync after model writes (save/continue restore, hide/show remount).
    queueMicrotask(() => {
      if (this.ngControl?.control) {
        this.ngControl.control.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  // --- VALIDATION ---

  /**
   * Validation state computed - determines which rows have errors and table-level error message. Matches table-grid
   * validation logic exactly.
   */
  protected readonly validationState = computed(() => {
    const responses = this.rowValues();
    const mode = this.requiredMode();
    const rows = this.rows();

    const result = {
      isValid: true,
      rowErrorIds: new Set<string>(),
      tableErrorMessage: null as string | null,
    };

    if (mode === 'none') return result;

    if (mode === 'all-rows') {
      // Every enabled column in every row must be answered
      rows.forEach((row) => {
        if (!this._rowHasAllAnswers(row, responses)) {
          result.rowErrorIds.add(row.linkId);
        }
      });
      if (result.rowErrorIds.size > 0) {
        result.isValid = false;
        result.tableErrorMessage = this.validationMessage() || 'All rows must be answered.';
      }
    } else if (mode === 'custom') {
      // Only rows with required: true are mandatory
      rows.forEach((row) => {
        if (row.required === true && !this._rowHasAnyAnswer(row, responses)) {
          result.rowErrorIds.add(row.linkId);
        }
      });
      if (result.rowErrorIds.size > 0) {
        result.isValid = false;
        result.tableErrorMessage = this.validationMessage() || 'Required rows must be answered.';
      }
    } else if (mode === 'at-least-one-yes') {
      // Check if validationTargetValue is explicitly configured
      const configuredTarget = this.validationTargetValue();
      const hasExplicitTarget = configuredTarget && configuredTarget.trim().length > 0;

      let matchCount = 0;

      if (hasExplicitTarget) {
        // Strict mode: look for specific target value
        rows.forEach((row) => {
          if (this._rowHasTargetValue(row, responses, configuredTarget!)) {
            matchCount++;
          }
        });
      } else {
        // Flexible mode: all columns in at least one row must be answered
        rows.forEach((row) => {
          if (this._rowHasAllAnswers(row, responses)) {
            matchCount++;
          }
        });
      }

      if (matchCount === 0) {
        result.isValid = false;
        // Generate user-friendly message based on column type
        const defaultMessage = hasExplicitTarget
          ? this._getAtLeastOneValidationMessage(configuredTarget!)
          : 'At least one row must be answered.';
        result.tableErrorMessage = this.validationMessage() || defaultMessage;

        // Only flag rows the user has actually started answering but hasn't completed/matched yet. Untouched rows
        // aren't individually wrong, so don't tint the entire table in error state for a table-level requirement.
        rows.forEach((row) => {
          if (!this._rowHasAnyAnswer(row, responses)) return;

          const satisfied = hasExplicitTarget
            ? this._rowHasTargetValue(row, responses, configuredTarget!)
            : this._rowHasAllAnswers(row, responses);

          if (!satisfied) {
            result.rowErrorIds.add(row.linkId);
          }
        });
      }
    }

    return result;
  });

  validateTable(): { valid: boolean; errors: CellValidationError[] } {
    const state = this.validationState();
    const errors: CellValidationError[] = [];

    // Convert Set of row IDs to validation errors
    state.rowErrorIds.forEach((rowLinkId) => {
      errors.push({
        rowId: rowLinkId,
        message: 'Required',
      });
    });

    // Check dropdown-with-label internal validation errors
    this.dropdowns().forEach((dropdown) => {
      const idVal = dropdown.id();
      if (!idVal || !idVal.includes('::')) return;

      const parts = idVal.split('::');
      const rowId = parts[0];
      const colId = parts.slice(1).join('::');

      if (!rowId || !colId) return;
      if (!this.isCellEnabled(rowId, colId)) return;

      if (dropdown._invalid()) {
        const dropdownErrors = dropdown.errors;
        let message = 'Required';
        if (dropdownErrors) {
          if (dropdownErrors['otherRequired']) {
            message = dropdown.getValidationMessage('otherRequired') || 'Please specify a value for "Other"';
          } else if (dropdownErrors['required']) {
            message = dropdown.getValidationMessage('required') || 'This field is required';
          } else if (dropdownErrors['minSelection']) {
            message = dropdown.getValidationMessage('minSelection') || 'Min selection error';
          } else if (dropdownErrors['maxSelection']) {
            message = dropdown.getValidationMessage('maxSelection') || 'Max selection error';
          }
        }

        errors.push({
          rowId,
          columnId: colId,
          message,
        });
      }
    });

    const hasDropdownErrors = errors.length > 0;

    return {
      valid: state.isValid && !hasDropdownErrors,
      errors,
    };
  }

  IsRowInvalid(row: EnhancedRowData): boolean {
    const state = this.validationState();

    if (state.rowErrorIds.has(row.linkId)) {
      return true;
    }

    // Use the reactive computed signal for dropdown validity.
    // This ensures the template is properly notified when dropdown validity changes
    // (critical for backward compatibility with existing templates).
    const dropdownValidity = this.dropdownValidityByRow();
    return dropdownValidity[row.linkId] === true;
  }

  shouldShowError(row: EnhancedRowData): boolean {
    // Check if any dropdown in this row is both touched AND invalid.
    // This enables immediate red background when "Other" is selected from a dropdown,
    // without waiting for the entire table grid to be touched.
    const dropdownTouchedInvalid = this.dropdownTouchedInvalidByRow();
    if (dropdownTouchedInvalid[row.linkId] === true) {
      return true;
    }

    const isExternalTouched = this.ngControl?.touched === true;
    const isInternalTouched = this.isTouched();
    const forceShow = this.forceShowErrors();

    if (!forceShow && !isInternalTouched && !isExternalTouched) {
      return false;
    }

    return this.IsRowInvalid(row);
  }

  shouldShowTableError(): boolean {
    const state = this.validationState();
    if (!state.tableErrorMessage) {
      return false;
    }

    const isExternalTouched = this.ngControl?.touched === true;
    const isInternalTouched = this.isTouched();
    const forceShow = this.forceShowErrors();

    return forceShow || isInternalTouched || isExternalTouched;
  }

  // --- VALIDATION HELPERS ---

  /** Check if a row has any answer (any enabled cell has a non-empty value). */
  private _rowHasAnyAnswer(row: EnhancedRowData, responses: Record<string, Record<string, CellDataOrValue>>): boolean {
    const columns = this.columns();
    for (const column of columns) {
      if (this.isCellEnabled(row.linkId, column.id)) {
        const cellValue = this.getCellValue(row.linkId, column.id);
        if (!this.isCellValueEmpty(cellValue, column.inputType)) {
          return true;
        }
      }
    }
    return false;
  }

  /** Check if every enabled cell in a row has a non-empty value (used by all-rows mode). */
  private _rowHasAllAnswers(row: EnhancedRowData, responses: Record<string, Record<string, CellDataOrValue>>): boolean {
    const columns = this.columns();
    const enabledColumns = columns.filter((col) => this.isCellEnabled(row.linkId, col.id));

    // For radio columns: at least one must be answered (they are mutually exclusive Yes/No choices)
    const radioColumns = enabledColumns.filter((col) => col.inputType === 'radio');
    if (radioColumns.length > 0) {
      const hasRadioAnswer = radioColumns.some((col) => {
        const value = this.getCellValue(row.linkId, col.id);
        return value !== null && value !== undefined;
      });
      if (!hasRadioAnswer) return false;
    }

    // For all non-radio enabled columns: every one must be answered
    for (const column of enabledColumns) {
      if (column.inputType === 'radio') continue;
      const cellValue = this.getCellValue(row.linkId, column.id);
      if (this.isCellValueEmpty(cellValue, column.inputType)) {
        return false;
      }
    }
    return true;
  }

  /** Check if a row has a specific target value in any enabled cell. */
  private _rowHasTargetValue(
    row: EnhancedRowData,
    responses: Record<string, Record<string, CellDataOrValue>>,
    targetValue: string
  ): boolean {
    const columns = this.columns();
    for (const column of columns) {
      if (this.isCellEnabled(row.linkId, column.id)) {
        const cellValue = this.getCellValue(row.linkId, column.id);
        if (this._valueMatchesTarget(cellValue, targetValue, column.inputType)) {
          return true;
        }
      }
    }
    return false;
  }

  /** Check if a cell value matches the target value for at-least-one-yes validation. */
  private _valueMatchesTarget(value: CellValue, target: string, inputType: ColumnInputType): boolean {
    if (value === null || value === undefined) return false;

    const normalizedTarget = target.toLowerCase().trim();

    if (inputType === 'checkbox') {
      // For checkboxes, any truthy value counts as "yes"
      // The target for checkbox is typically the column ID, so just check if checked
      return value === true;
    }

    if (inputType === 'radio' || inputType === 'dropdown') {
      // For radio/dropdown, check if the selected value matches target
      const strValue = String(value).toLowerCase().trim();
      return strValue === normalizedTarget || this._isAffirmativeString(strValue);
    }

    if (inputType === 'text') {
      // For text input, exact match
      const strValue = String(value).toLowerCase().trim();
      return strValue === normalizedTarget;
    }

    if (inputType === 'rating') {
      // For rating, check numeric match
      if (typeof value === 'number') {
        return String(value) === target;
      }
    }

    return false;
  }

  /**
   * Resolve validation target value for at-least-one-yes mode. If validationTargetValue is set, use it. Otherwise,
   * auto-detect from first column.
   */
  private _resolveValidationTargetValue(): string {
    const configuredTarget = this.validationTargetValue();
    if (configuredTarget && configuredTarget.trim().length > 0) {
      return configuredTarget.trim();
    }

    // Auto-detect: look for first affirmative column
    const columns = this.columns();
    for (const col of columns) {
      if (col.inputType === 'checkbox' || col.inputType === 'radio') {
        // For checkboxes, use column ID
        if (col.inputType === 'checkbox') {
          return col.id;
        }

        // For radio, find first affirmative option
        const cellControls = this.cellControls();
        const firstRowId = this.rows()[0]?.linkId;
        if (firstRowId && cellControls[firstRowId]?.[col.id]) {
          const cellControl = cellControls[firstRowId][col.id];
          const opts = cellControl?.options || [];
          const affirmativeOpt = opts.find((opt) => this._isAffirmativeString(String(opt.value || opt.label)));
          if (affirmativeOpt) {
            return String(affirmativeOpt.value || affirmativeOpt.label);
          }
        }
      }
    }

    return 'yes'; // Fallback
  }

  /** Check if a string represents an affirmative value (yes, y, true, 1, etc.) */
  private _isAffirmativeString(value: string): boolean {
    const normalized = value.toLowerCase().replace(/\s+/g, '');
    return (
      normalized === 'yes' ||
      normalized === 'y' ||
      normalized === 'true' ||
      normalized === 't' ||
      normalized === '1' ||
      normalized === 'ok'
    );
  }

  /**
   * Generate user-friendly validation message for at-least-one-yes mode. Checks if target is a column ID for checkboxes
   * to provide better messaging.
   */
  private _getAtLeastOneValidationMessage(targetValue: string): string {
    // Check if target matches a column ID (for checkbox columns)
    const columns = this.columns();
    const targetColumn = columns.find((col) => col.id === targetValue);

    if (targetColumn?.inputType === 'checkbox') {
      return 'At least one checkbox must be checked.';
    }

    // For other types, use the target value in the message
    return `At least one row must have '${targetValue}'.`;
  }

  isCellValueEmpty(value: CellValue, inputType: ColumnInputType): boolean {
    if (value === null || value === undefined) {
      return true;
    }
    switch (inputType) {
      case 'text':
        return typeof value === 'string' && value.trim() === '';
      case 'checkbox':
        return value !== true; // For checkboxes, only true is considered a non-empty value
      case 'radio':
        return false;
      case 'dropdown':
        if (typeof value === 'string' && value.trim() === '') {
          return true;
        }
        if (Array.isArray(value)) {
          return value.length === 0;
        }
        return false;
      case 'rating':
        return typeof value === 'number' && value === 0;
      case 'date':
        if (typeof value === 'string') {
          return value.trim() === '';
        }
        if (value && typeof value === 'object' && 'start' in value && 'end' in value) {
          // Range dates only count as answered once both bounds are set.
          const range = value as { start: string | null; end: string | null };
          return !range.start || !range.end;
        }
        return false;
      default:
        return false;
    }
  }

  protected override _validate(control: AbstractControl): ValidationErrors | null {
    const validation = this.validateTable();
    return validation.valid ? null : { hxConditionalTableGrid: { valid: false, errors: validation.errors } };
  }

  // --- Private Methods ---

  private initializeFromResponses(): void {
    const initial = this.initialResponses();
    if (initial && Object.keys(initial).length > 0) {
      this.rowValues.set(this.recalculateCalculatedDates({ ...initial }));
    }
  }

  private emitResponse(): void {
    const validation = this.validateTable();
    const response: ConditionalTableGridResponse = {
      tableId: this.tableId(),
      responses: this.rowValues(),
      cellStates: this.cellStates(),
      valid: validation.valid,
      errors: validation.errors,
      timestamp: new Date(),
    };

    this.responseChange.emit(response);
    this.cellStateChange.emit(this.cellStates());

    if (this._onChange) {
      this._onChange({
        responses: this.rowValues(),
        cellStates: this.cellStates(),
      });
    }
  }
}
